/**
 * 卡牌选择 Hook
 * 
 * 处理换牌选择、弃牌选择逻辑
 */

import { useState, useCallback } from 'react';
import type { GameCard } from '../../types';
import { toggleCardSelection } from '../../utils/cardHelpers';

interface UseCardSelectionOptions {
  maxSwapSelect: number;  // max cards selectable in swap (1-5)
}

interface UseCardSelectionReturn {
  // Swap 状态 (从牌堆选牌)
  selectedSwapIds: Set<string>;
  selectedSwapCount: number;
  canConfirmSwap: boolean;
  toggleSwapCard: (card: GameCard) => void;
  getSelectedSwapIds: () => string[];
  resetSwapSelection: () => void;
  
  // Discard 状态 (弃牌)
  selectedDiscardIds: Set<string>;
  selectedDiscardCount: number;
  toggleDiscardCard: (card: GameCard, needToDiscard: number) => void;
  getSelectedDiscardIds: () => string[];
  resetDiscardSelection: () => void;
}

export function useCardSelection(options: UseCardSelectionOptions): UseCardSelectionReturn {
  const { maxSwapSelect } = options;
  
  // Swap 选择状态
  const [selectedSwapIds, setSelectedSwapIds] = useState<Set<string>>(new Set());
  
  // Discard 选择状态
  const [selectedDiscardIds, setSelectedDiscardIds] = useState<Set<string>>(new Set());

  // Swap 相关方法
  const toggleSwapCard = useCallback((card: GameCard) => {
    setSelectedSwapIds((prev) => toggleCardSelection(prev, card.id, maxSwapSelect));
  }, [maxSwapSelect]);

  const getSelectedSwapIds = useCallback((): string[] => {
    return Array.from(selectedSwapIds);
  }, [selectedSwapIds]);

  const resetSwapSelection = useCallback(() => {
    setSelectedSwapIds(new Set());
  }, []);

  // Discard 相关方法
  const toggleDiscardCard = useCallback((card: GameCard, needToDiscard: number) => {
    setSelectedDiscardIds((prev) => toggleCardSelection(prev, card.id, needToDiscard));
  }, []);

  const getSelectedDiscardIds = useCallback((): string[] => {
    return Array.from(selectedDiscardIds);
  }, [selectedDiscardIds]);

  const resetDiscardSelection = useCallback(() => {
    setSelectedDiscardIds(new Set());
  }, []);

  // 计算值
  const selectedSwapCount = selectedSwapIds.size;
  const canConfirmSwap = selectedSwapCount > 0 && selectedSwapCount <= maxSwapSelect;
  
  const selectedDiscardCount = selectedDiscardIds.size;

  return {
    // Swap
    selectedSwapIds,
    selectedSwapCount,
    canConfirmSwap,
    toggleSwapCard,
    getSelectedSwapIds,
    resetSwapSelection,
    
    // Discard
    selectedDiscardIds,
    selectedDiscardCount,
    toggleDiscardCard,
    getSelectedDiscardIds,
    resetDiscardSelection,
  };
}
